import React from 'react';
import styled from '@emotion/styled';
import { devices } from '../common';
import { RichTextContent } from '../StyledComponents';
import { HomeCardCarousel } from '../../@types/types';

/**
 * Client-feedback section on the home page. Same card visuals as campaign
 * CardCarousel, but without a section title or per-card headers.
 */

type Props = {
  data?: HomeCardCarousel | null;
};

const DEFAULT_SECTION_FONT = '#000000';
const hasBg = (c?: string) => c != null && c !== '';
const sectionFont = (c?: string) => c ?? DEFAULT_SECTION_FONT;

const CardCarouselSection = ({ data }: Props) => {
  if (!data || !data.Cards || data.Cards.length === 0) return null;

  const cardBg = data.CardBackgroundColor;
  const cardFg = data.CardFontColor;

  return (
    <SectionContainer
      backgroundColor={data.BackgroundColor}
      fontColor={sectionFont(data.FontColor)}
      lightestBlue={!hasBg(data.BackgroundColor)}
    >
      <TextSection>
        <CustomerCardRow>
          {data.Cards.map((item, index) => (
            <CustomerCard
              key={index}
              backgroundColor={cardBg}
              fontColor={sectionFont(cardFg)}
            >
              <RichTextContent>{item.Content}</RichTextContent>
            </CustomerCard>
          ))}
        </CustomerCardRow>
      </TextSection>
    </SectionContainer>
  );
};

export default CardCarouselSection;

const SectionContainer = styled.div<{
  lightestBlue?: boolean;
  backgroundColor?: string;
  fontColor?: string;
}>`
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) =>
    hasBg(props.backgroundColor)
      ? props.backgroundColor
      : props.lightestBlue
      ? props.theme.colors.lightestBlue
      : props.theme.colors.lighterBlue};
  color: ${(props) => props.fontColor ?? DEFAULT_SECTION_FONT};
  height: max-content;

  @media ${devices.mobileXL} {
    height: auto;
    display: block;
  }
`;

const TextSection = styled.div`
  padding: 40px 80px;
  height: auto;
  display: block;
  flex: 1;
  min-width: 0;

  @media ${devices.tablet} {
    padding: ${(props) => props.theme.spacing.default};
  }

  @media ${devices.mobileXL} {
    width: 95%;
  }
`;

const CustomerCardRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;

  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

const CustomerCard = styled.div<{
  backgroundColor?: string;
  fontColor?: string;
}>`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.fontColor ?? DEFAULT_SECTION_FONT};
  background-color: ${(props) =>
    hasBg(props.backgroundColor)
      ? props.backgroundColor
      : props.theme.colors.lightestBlue};
  box-shadow: 0px 5px 15px rgba(2, 38, 64, 0.1);
  border-radius: 28px;
  padding: 28px;
  gap: 8px;
`;
